import { useCallback, useMemo } from "react";

export interface AnnotationTargetShortcutItem {
  readonly id: string;
}

/**
 * Structural key event accepted from both React and native keyboard handlers. Consumers choose
 * the DOM scope; the target strip uses it locally and composite surfaces may reuse it more widely.
 */
export interface AnnotationTargetShortcutKeyEvent {
  readonly key: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly defaultPrevented: boolean;
  readonly target: EventTarget | null;
  readonly isComposing?: boolean;
  readonly keyCode?: number;
  readonly nativeEvent?: {
    readonly isComposing?: boolean;
    readonly keyCode?: number;
  };
  preventDefault: () => void;
  stopPropagation: () => void;
}

export interface UseAnnotationTargetShortcutsOptions {
  targets: readonly AnnotationTargetShortcutItem[];
  selectedTargetId: string | null;
  selectedOnly: boolean;
  disabled?: boolean;
  onSelectedOnlyChange: (selectedOnly: boolean) => void;
  onVisibleTargetIdsChange: (targetIds: readonly string[]) => void;
  onTargetChange: (targetId: string) => void;
}

export interface AnnotationTargetShortcuts {
  /** Returns whether the event was consumed. */
  onKeyDown: (event: AnnotationTargetShortcutKeyEvent) => boolean;
  /** Active shortcuts for the owning target-selection scope. */
  ariaKeyShortcuts: string;
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const editingTarget = target.closest<HTMLElement>(
    "input, textarea, select, [contenteditable='true']",
  );
  if (editingTarget === null) return false;
  return !(editingTarget instanceof HTMLInputElement && editingTarget.type === "range");
}

function isComposing(event: AnnotationTargetShortcutKeyEvent): boolean {
  return (
    event.isComposing === true ||
    event.keyCode === 229 ||
    event.nativeEvent?.isComposing === true ||
    event.nativeEvent?.keyCode === 229
  );
}

/**
 * Object-slot shortcuts shared by the standalone strip and wider consumer scopes.
 *
 * `1`–`9` select the corresponding target and `0` toggles selected-only visibility. When the
 * command starts on a strip selection button, focus follows the new selection; a wider scope
 * leaves its existing focus untouched.
 */
export function useAnnotationTargetShortcuts({
  targets,
  selectedTargetId,
  selectedOnly,
  disabled = false,
  onSelectedOnlyChange,
  onVisibleTargetIdsChange,
  onTargetChange,
}: UseAnnotationTargetShortcutsOptions): AnnotationTargetShortcuts {
  const onKeyDown = useCallback(
    (event: AnnotationTargetShortcutKeyEvent): boolean => {
      if (
        disabled ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isComposing(event) ||
        isTextEditingTarget(event.target)
      ) {
        return false;
      }

      const key = event.key;
      if (key !== "0" && !/^[1-9]$/.test(key)) return false;

      event.preventDefault();
      event.stopPropagation();

      const shortcutFocusTarget =
        event.target instanceof HTMLElement
          ? event.target.closest<HTMLElement>("[data-annotation-target-select]")
          : null;

      if (key === "0") {
        shortcutFocusTarget?.setAttribute("data-annotation-shortcut-focus", "");
        const nextSelectedOnly = !selectedOnly;
        onSelectedOnlyChange(nextSelectedOnly);
        onVisibleTargetIdsChange(
          nextSelectedOnly
            ? selectedTargetId === null
              ? []
              : [selectedTargetId]
            : targets.map((target) => target.id),
        );
        return true;
      }

      const shortcutIndex = Number(key) - 1;
      const shortcutTarget = targets[shortcutIndex];
      if (!shortcutTarget) {
        shortcutFocusTarget?.setAttribute("data-annotation-shortcut-focus", "");
        return true;
      }

      onTargetChange(shortcutTarget.id);
      if (shortcutFocusTarget) {
        const targetStrip = shortcutFocusTarget.closest<HTMLElement>(
          "[data-annotation-target-strip]",
        );
        const nextTarget = targetStrip?.querySelector<HTMLElement>(
          `[data-annotation-slot="${shortcutIndex + 1}"] [data-annotation-target-select]`,
        );
        nextTarget?.setAttribute("data-annotation-shortcut-focus", "");
        nextTarget?.focus({ preventScroll: true });
      }
      return true;
    },
    [
      disabled,
      onSelectedOnlyChange,
      onTargetChange,
      onVisibleTargetIdsChange,
      selectedOnly,
      selectedTargetId,
      targets,
    ],
  );

  const ariaKeyShortcuts = useMemo(
    () =>
      disabled || targets.length === 0
        ? ""
        : ["0", ...targets.slice(0, 9).map((_, index) => String(index + 1))].join(" "),
    [disabled, targets],
  );

  return { onKeyDown, ariaKeyShortcuts };
}
